import React from 'react';
import {Check} from "@sparkware/app-utils";
import {observer} from 'mobx-react';
import {GameInfo} from '../../../../../services/games/game-info';
import {GameImageBox} from '../../boxes/game-image/game-image.box';
import {GameImageAnimatedComponent} from './game-image-animated.component';
import { OrbitComponentWithState } from '../../../../base-components/orbit-component-with-state/orbit-component-with-state';
import {SINGLE_GAME_IMAGE} from '../../../css-constants';
import { AspectRatioContainerComponent } from '../../../../raw-components/aspect-ratio-container/aspect-ratio-container.component';

export type GameIconAnimationState = "ab-disabled" | "unavailable" | "static" | "animated";

export interface GameIconComponentProps {
    gameInfo: GameInfo;
    useSquareIcons?: boolean;
    useLazyLoading?: boolean;
    onLoad?(): void;
    onLoadStart?(): void;
    onSettled?(): void;
    onAnimationIconStateChange?(state: GameIconAnimationState): void;
}

export interface GameIconComponentState {
    loadingAnimation?: boolean;
    animationJsonUrl?: string;
    hideStaticImage?: boolean;
}

export const GameImageComponent = observer(
    class GameImageComponent extends OrbitComponentWithState<GameIconComponentProps, GameIconComponentState> {
        constructor(props: GameIconComponentProps) {
            super(props);
            this.state = {};
        }

        componentDidMount() {
            if (this.props.onAnimationIconStateChange) {
                this.props.onAnimationIconStateChange(!this.state.animationJsonUrl ? "unavailable" : "static");
            }
        }

        private _loadAnimatedIconTimeout = null;

        componentWillUnmount() {
            super.componentWillUnmount();
            if(this._loadAnimatedIconTimeout) {
                clearTimeout(this._loadAnimatedIconTimeout);
                this._loadAnimatedIconTimeout = null;
            }
        }
        private onSettled = () => {
            this.setState({loadingAnimation: false});
            if (this.props.onSettled) {
                this.props.onSettled();
            }
            this.attemptLoadAnimatedIcon();
        };

        private attemptLoadAnimatedIcon = () => {
            this._loadAnimatedIconTimeout = setTimeout(() => {
                let data = this.props.gameInfo.getGameImage({squareIcon: this._shouldUseSquareIcons(), animatedIcon: true});
                if (data) {
                    this.setState({animationJsonUrl: data});
                }
                this._loadAnimatedIconTimeout = null;
            }, this.props.gameInfo.getAnimatedImageTimings().delay || 0);
        };

        private onAnimationLoad = () => {
            this.setState({hideStaticImage: true});
            if (this.props.onAnimationIconStateChange) {
                this.props.onAnimationIconStateChange("animated");
            }
        };

        private onAnimationWillUnload = () => {
            this.setState({hideStaticImage: false});
            if (this.props.onAnimationIconStateChange) {
                this.props.onAnimationIconStateChange("static");
            }
        };

        private _shouldUseSquareIcons = () => {
            if (Check.isNullOrUndefined(this.props.useSquareIcons)) {
                return this.services.theme.shouldUseSquareIcons;
            }
            return this.props.useSquareIcons;
        }

        render() {
            const seoAlt = this.props.gameInfo.getImageSeoAlt();
            const imgUrl = this.props.gameInfo.getGameImage({squareIcon: this._shouldUseSquareIcons()});
            const useLazyLoad = Check.isNullOrUndefined(this.props.useLazyLoading) ? true : this.props.useLazyLoading;

            return <AspectRatioContainerComponent
                {...this.services.games.getGameImageExpectedSizes(this._shouldUseSquareIcons())}
                isLoading={this.state.loadingAnimation}
            >
                { this.state.animationJsonUrl ? <GameImageAnimatedComponent
                    animationJsonUrl={this.state.animationJsonUrl}
                    animationTiming={this.props.gameInfo.getAnimatedImageTimings()}
                    useSquareIcons={this._shouldUseSquareIcons()}
                    {...this.services.games.getGameImageExpectedSizes(this._shouldUseSquareIcons(), true)}
                    onAnimationLoad={this.onAnimationLoad}
                    onAnimationWillUnload={this.onAnimationWillUnload}
                /> : null }
                <GameImageBox
                    hide={this.state.hideStaticImage}
                    src={imgUrl}
                    alt={seoAlt}
                    lazy = {useLazyLoad}
                    className={`cy-game-image ${SINGLE_GAME_IMAGE}`}
                    fallbackSrc={this.props.gameInfo.getGameImagePlaceHolderUrl(this._shouldUseSquareIcons())}
                    {...this.services.games.getGameImageExpectedSizes(this._shouldUseSquareIcons())}
                    squareIcon={this._shouldUseSquareIcons()}
                    onLoadStart={() => {
                        this.setState({loadingAnimation: true});
                    }}
                    onLoad={this.props.onLoad}
                    onSettled={this.onSettled}
                />
            </AspectRatioContainerComponent>;
        }
    }
);
